import React, { useEffect, useState } from "react";
import closeIcon from "../../../assets/crossIcon.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import checkedIcon from "../../../assets/close-button.svg";

const dummyMappedProcedures = Array.from({ length: 18 }).map((_, i) => ({
  id: i + 1,
  name:
    i % 2 === 0
      ? "Balloon Pulmonary / Aortic Valvotomy - Balloon Aortic Valvotomy"
      : "Pyogenic Meningitis",
}));

export default function InactiveProcedureModal({
  open,
  onClose,
  onConfirm,
  type,
  confirmOnly = false,
  confirmLoading = false,
}) {
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "auto";
    return () => (document.body.style.overflow = "auto");
  }, [open]);

  if (!open) return null;

  // Simple confirmation (used for toggle when mapped procedures list not ready yet).
  // When ready, parent can pass confirmOnly={false} to show full modal with list of mapped procedures.
  if (confirmOnly) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
        <div className="relative w-full max-w-md bg-white rounded-3xl shadow-xl px-8 py-8">
          <button
            onClick={onClose}
            className="absolute top-6 right-6 bg-transparent border-none p-0"
          >
            <img src={closeIcon} alt="close" className="w-5 h-5" />
          </button>
          <p className="text-center font-lufga font-medium text-gray-800 text-lg mt-2 pr-8">
            Are you sure, you want to inactive the Procedure ?
          </p>
          <div className="flex justify-center mt-8">
            <button
              onClick={async () => {
                await onConfirm?.();
                onClose();
              }}
              disabled={confirmLoading}
              className="rounded-full px-8 py-4 border border-solid border-[#5AC69F] bg-[#76d7c4] hover:bg-[#5AC69F] font-lufga font-medium text-black transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {confirmLoading ? "Inactivating..." : "Yes, Inactive Procedure"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const filteredList = dummyMappedProcedures.filter((p) =>
    p.name.toLowerCase().includes(search.toLowerCase()),
  );

  const toggleItem = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (selected.length === filteredList.length) {
      setSelected([]);
    } else {
      setSelected(filteredList.map((p) => p.id));
    }
  };

  const handleConfirmInactive = () => {
    onConfirm(); // ✅ parent handles status change
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-[#5AC69F80] p-4 overflow-y-auto">
      <div className="relative w-full max-w-[1100px] max-h-[90vh] bg-white rounded-[24px] px-6 sm:px-10 py-8 flex flex-col min-h-0">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 bg-transparent border-none p-0 z-10"
        >
          <img src={closeIcon} alt="close" className="w-5 h-5" />
        </button>

        {/* Title */}
        <h2 className="text-center font-lufga font-medium text-[20px] text-black flex-shrink-0">
          Do you want to inactive {type} ?
        </h2>

        {/* Info */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-6 flex-shrink-0">
          <p className="font-lufga font-medium text-lg sm:text-[28px] leading-[100%] tracking-[-0.04em] text-[#494949] text-center">
            It will impact on the below Mapped Procedures
          </p>

          <span className="font-lufga text-[14px] text-[#38B58A] text-center sm:text-left">
            Total Mapped Procedures ({filteredList.length})
          </span>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 min-h-0 overflow-y-auto mt-4">
          {/* Search */}
          <div className="flex items-center gap-[10px] w-full h-[56px] sm:h-[90px] rounded-[54px] px-4 sm:px-[24px] bg-[#FAFAFA] border-2 border-[#CFCFCF]">
            <img src={searchIcon} alt="search" className="w-5 h-5 sm:w-[32px] sm:h-[32px] flex-shrink-0" />
            <input
              placeholder="Procedure Name"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent outline-none border-none font-lufga font-normal text-sm sm:text-[16px] leading-[100%] tracking-[-0.04em] text-[#494949] placeholder-[#9A9A9A]"
            />
          </div>

          <div className="rounded-[54px] border-4 border-black p-4 sm:p-6 mt-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span className="font-lufga text-[14px] text-black">
                Mapped Procedure Name
              </span>

              <button
                onClick={toggleAll}
                className="flex border-none outline-none items-center gap-2 font-lufga text-[13px] text-black"
              >
                <div className="w-6 h-6 rounded-full border-2 border-[#38B58A] flex items-center justify-center bg-[#38B58A]">
                  <span className="text-white text-[12px]">✓</span>
                </div>
                Select All
              </button>
            </div>

            {/* List */}
            <div className="mt-3 border border-[#E6E6E6] rounded-[16px] max-h-[280px] sm:max-h-[360px] overflow-y-auto">
              {filteredList.map((item, idx) => (
                <div
                  key={item.id}
                  className={`flex items-center gap-4 px-4 sm:px-6 py-3 sm:py-4 ${
                    idx % 2 === 0 ? "bg-white" : "bg-[#FAFAFA]"
                  }`}
                >
                  <button
                    onClick={() => toggleItem(item.id)}
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      selected.includes(item.id)
                        ? "bg-[#38B58A] border-[#38B58A]"
                        : "border-black"
                    }`}
                  >
                    {selected.includes(item.id) && (
                      <span className="text-white text-[12px]">✓</span>
                    )}
                  </button>

                  <p className="font-lufga text-[14px] text-gray-800">
                    {item.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="flex justify-center mt-6 sm:mt-8 flex-shrink-0">
          <button
            onClick={handleConfirmInactive}
            className="rounded-[54px] px-[42px] py-[14px] bg-[#7AD3AE] font-lufga font-medium text-[14px] text-black hover:opacity-90 transition border-[#5AC69F] border-solid"
          >
            Yes, Inactive Procedure
          </button>
        </div>
      </div>
    </div>
  );
}
